import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Alert,
  Stack,
  Slider,
  Paper,
  FormControlLabel,
  Switch,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import QRCodeStyling, {
  DotType,
  CornerSquareType,
  CornerDotType,
  FileExtension,
} from 'qr-code-styling';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';
import StyledSelect from './shared/StyledSelect';

const dotStyles: { value: DotType; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dots', label: 'Dots' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
];

const cornerSquareStyles: { value: CornerSquareType; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
];

const cornerDotStyles: { value: CornerDotType; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
];

const errorCorrectionLevels = [
  { value: 'L', label: 'Low (7%)' },
  { value: 'M', label: 'Medium (15%)' },
  { value: 'Q', label: 'Quartile (25%)' },
  { value: 'H', label: 'High (30%)' },
];

const QrCodeUtility: React.FC = () => {
  const [input, setInput] = useState(() => localStorage.getItem('qr-input') || '');
  const [size, setSize] = useState(() => parseInt(localStorage.getItem('qr-size') || '256', 10));
  const [includeMargin, setIncludeMargin] = useState(() => localStorage.getItem('qr-margin') === 'true');
  const [fgColor, setFgColor] = useState(() => localStorage.getItem('qr-fg-color') || '#000000');
  const [bgColor, setBgColor] = useState(() => localStorage.getItem('qr-bg-color') || '#ffffff');
  const [dotStyle, setDotStyle] = useState<DotType>(() => (localStorage.getItem('qr-dot-style') as DotType) || 'square');
  const [cornerSquareStyle, setCornerSquareStyle] = useState<CornerSquareType>(() => (localStorage.getItem('qr-corner-square') as CornerSquareType) || 'square');
  const [cornerDotStyle, setCornerDotStyle] = useState<CornerDotType>(() => (localStorage.getItem('qr-corner-dot') as CornerDotType) || 'square');
  const [errorLevel, setErrorLevel] = useState<'L' | 'M' | 'Q' | 'H'>(() => (localStorage.getItem('qr-error-level') as 'L' | 'M' | 'Q' | 'H') || 'M');
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem('qr-logo') || '');
  const [downloadFormat, setDownloadFormat] = useState<FileExtension>('png');
  const [error, setError] = useState('');

  const qrRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    localStorage.setItem('qr-input', input);
    localStorage.setItem('qr-size', size.toString());
    localStorage.setItem('qr-margin', includeMargin.toString());
    localStorage.setItem('qr-fg-color', fgColor);
    localStorage.setItem('qr-bg-color', bgColor);
    localStorage.setItem('qr-dot-style', dotStyle);
    localStorage.setItem('qr-corner-square', cornerSquareStyle);
    localStorage.setItem('qr-corner-dot', cornerDotStyle);
    localStorage.setItem('qr-error-level', errorLevel);
    localStorage.setItem('qr-logo', logoUrl);
  }, [input, size, includeMargin, fgColor, bgColor, dotStyle, cornerSquareStyle, cornerDotStyle, errorLevel, logoUrl]);

  useEffect(() => {
    qrCode.current = new QRCodeStyling({
      width: 256,
      height: 256,
      data: 'https://example.com',
      imageOptions: { crossOrigin: 'anonymous', margin: 5 },
    });
    if (qrRef.current) {
      qrRef.current.innerHTML = '';
      qrCode.current.append(qrRef.current);
    }
  }, []);

  useEffect(() => {
    if (qrCode.current) {
      qrCode.current.update({
        width: size,
        height: size,
        data: input || 'https://example.com',
        margin: includeMargin ? 10 : 0,
        qrOptions: { errorCorrectionLevel: errorLevel },
        dotsOptions: { color: fgColor, type: dotStyle },
        backgroundOptions: { color: bgColor },
        cornersSquareOptions: { color: fgColor, type: cornerSquareStyle },
        cornersDotOptions: { color: fgColor, type: cornerDotStyle },
        image: logoUrl || undefined,
      });
    }
  }, [input, size, includeMargin, fgColor, bgColor, dotStyle, cornerSquareStyle, cornerDotStyle, errorLevel, logoUrl]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setLogoUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => setLogoUrl('');

  const downloadQrCode = () => {
    if (qrCode.current && input) qrCode.current.download({ extension: downloadFormat });
    else setError('Enter content to download QR code');
  };

  const clearAll = () => {
    setInput('');
    setSize(256);
    setIncludeMargin(false);
    setFgColor('#000000');
    setBgColor('#ffffff');
    setDotStyle('square');
    setCornerSquareStyle('square');
    setCornerDotStyle('square');
    setErrorLevel('M');
    setLogoUrl('');
    setError('');
  };

  return (
    <Box>
      <UtilityHeader 
        title="QR Code Generator" 
        description="Create highly customizable, professional-grade QR codes instantly." 
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: { xs: 4, lg: 6 }, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ flex: 1 }}>
          <Stack spacing={4}>
            <StyledTextArea
              label="Content"
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter text or URL..."
            />

            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 2, fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.65rem' }}>
                Size: {size}px
              </Typography>
              <Slider
                value={size}
                onChange={(_, newValue) => setSize(newValue as number)}
                min={128}
                max={512}
                step={8}
                valueLabelDisplay="auto"
                size="small"
              />
            </Box>

            <Stack spacing={3}>
              <StyledSelect 
                label="Dot Style" 
                value={dotStyle} 
                onChange={(e) => setDotStyle(e.target.value as DotType)}
              >
                {dotStyles.map((style) => <MenuItem key={style.value} value={style.value}>{style.label}</MenuItem>)}
              </StyledSelect>

              <Stack direction="row" spacing={2}>
                <StyledSelect 
                  label="Corner Square" 
                  value={cornerSquareStyle} 
                  onChange={(e) => setCornerSquareStyle(e.target.value as CornerSquareType)}
                >
                  {cornerSquareStyles.map((style) => <MenuItem key={style.value} value={style.value}>{style.label}</MenuItem>)}
                </StyledSelect>
                <StyledSelect 
                  label="Corner Dot" 
                  value={cornerDotStyle} 
                  onChange={(e) => setCornerDotStyle(e.target.value as CornerDotType)}
                >
                  {cornerDotStyles.map((style) => <MenuItem key={style.value} value={style.value}>{style.label}</MenuItem>)}
                </StyledSelect>
              </Stack>

              <StyledSelect 
                label="Error Correction" 
                value={errorLevel} 
                onChange={(e) => setErrorLevel(e.target.value as 'L' | 'M' | 'Q' | 'H')}
              >
                {errorCorrectionLevels.map((level) => <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>)}
              </StyledSelect>
            </Stack>

            <Stack direction="row" spacing={4}>
              <Box>
                <Typography gutterBottom variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>Foreground</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <input type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)} style={{ width: '40px', height: '32px', cursor: 'pointer', border: 'none', borderRadius: '6px', background: 'none' }} />
                  <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono' }}>{fgColor.toUpperCase()}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography gutterBottom variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>Background</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} style={{ width: '40px', height: '32px', cursor: 'pointer', border: 'none', borderRadius: '6px', background: 'none' }} />
                  <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono' }}>{bgColor.toUpperCase()}</Typography>
                </Box>
              </Box>
            </Stack>

            <Box>
              <FormControlLabel 
                control={<Switch checked={includeMargin} onChange={(e) => setIncludeMargin(e.target.checked)} size="small" />} 
                label={<Typography variant="body2" sx={{ color: 'text.secondary' }}>Include Margin</Typography>} 
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.65rem' }}>
                Center Logo (optional)
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <GlowButton variant="outlined" component="label" size="small" disableGlow sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                  Upload Logo
                  <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                </GlowButton>
                {logoUrl && <GlowButton variant="text" size="small" color="error" disableGlow onClick={removeLogo}>Remove</GlowButton>}
              </Stack>
              {logoUrl && <Box component="img" src={logoUrl} alt="Logo preview" sx={{ mt: 2, maxWidth: 80, maxHeight: 80, borderRadius: 2, border: '1px solid', borderColor: 'rgba(255,255,255,0.1)' }} />}
            </Box>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 4, border: '1px solid', borderColor: 'rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', width: '100%', overflow: 'hidden', position: 'relative', '&::before': { content: '""', position: 'absolute', inset: 0, opacity: 0.05, backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '20px 20px' } }}>
            <Box sx={{ p: 2, bgcolor: bgColor, borderRadius: 2, boxShadow: '0 10px 30px rgba(0,0,0,0.5)', lineHeight: 0, transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.02)' } }}>
              <div ref={qrRef} style={{ opacity: input ? 1 : 0.4 }} />
            </Box>
          </Paper>

          <Box sx={{ mt: 4, width: '100%' }}>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2.5 }}>
              <ToggleButtonGroup value={downloadFormat} exclusive onChange={(_, format) => format && setDownloadFormat(format)} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                <ToggleButton value="png">PNG</ToggleButton>
                <ToggleButton value="svg">SVG</ToggleButton>
                <ToggleButton value="jpeg">JPEG</ToggleButton>
                <ToggleButton value="webp">WEBP</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Stack direction="row" spacing={2}>
              <GlowButton fullWidth size="large" onClick={downloadQrCode} disabled={!input} startIcon={<Download />} sx={{ py: 1.5 }}>
                Download {downloadFormat.toUpperCase()}
              </GlowButton>
              <GlowButton variant="outlined" color="inherit" disableGlow onClick={clearAll} sx={{ opacity: 0.7, borderColor: 'rgba(255,255,255,0.1)' }}>Reset</GlowButton>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default QrCodeUtility;
